import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Shield, Upload, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FileUpload from '../components/FileUpload';
import FileList from '../components/FileList';
import UsageStats from '../components/UsageStats';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleUploadComplete = () => {
    // Trigger refresh of file list
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-neutral-50 to-primary-100">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <img
                src="/Declaro-logo-trans.png"
                alt="Declaro Logo"
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold text-neutral-900">Declaro</h1>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />

              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center text-warning-700 hover:text-warning-900 bg-warning-50 hover:bg-warning-100 px-3 py-2 rounded-lg transition-colors border border-warning-200"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Admin Panel</span>
                </button>
              )}

              <div className="flex items-center bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-200">
                {user?.is_admin ? (
                  <Shield className="h-4 w-4 mr-2 text-warning-600" />
                ) : (
                  <User className="h-4 w-4 mr-2 text-primary-600" />
                )}
                <span className="text-sm font-medium text-neutral-700">{user?.username}</span>
                {user?.is_admin && (
                  <span className="ml-2 bg-warning-100 text-warning-800 text-xs px-2 py-1 rounded-md font-medium">
                    Admin
                  </span>
                )}
              </div>

              <button
                onClick={logout}
                className="flex items-center text-neutral-600 hover:text-neutral-900 bg-neutral-50 hover:bg-neutral-100 px-3 py-2 rounded-lg transition-colors border border-neutral-200"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">{t('auth.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span>{t('dashboard.uploadFiles')}</span>
            </TabsTrigger>
            <TabsTrigger value="statistics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span>{t('dashboard.statistics')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <FileUpload onUploadComplete={handleUploadComplete} />
            <FileList refreshTrigger={refreshTrigger} />
          </TabsContent>

          <TabsContent value="statistics">
            <UsageStats />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;